/**
 * Auto Link Plugin for ProseMirror
 *
 * Automatically detects URLs in pasted text and typed content,
 * wrapping them with link marks. Processes text after paste to find
 * and linkify URLs.
 *
 * Limitations:
 * - URLs split across text nodes (due to formatting) won't be detected
 */

import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { schema } from "../schema";

export const autoLinkPluginKey = new PluginKey("autoLink");

// URL pattern that matches http:// and https:// URLs with valid domain structure
// Requires at least: protocol + domain with dot (e.g., example.com)
// Allows parentheses in URLs (for Wikipedia, GitHub, etc.)
// Excludes trailing punctuation that shouldn't be part of the URL
const URL_REGEX = /https?:\/\/[a-zA-Z0-9][-a-zA-Z0-9]*\.[^\s<>"[\]{}|\\^`]+[^\s<>"[\]{}|\\^`.,;:!?'"\]}-]/gi;

/**
 * Clean up URLs by removing trailing unbalanced closing parentheses.
 * This handles cases like "(see https://example.com)" where the trailing
 * paren belongs to the surrounding text, while preserving balanced parens
 * in URLs like "https://en.wikipedia.org/wiki/Foo_(bar)".
 */
function cleanupTrailingParens(url: string): string {
  let openCount = 0;
  let closeCount = 0;

  for (const char of url) {
    if (char === "(") openCount++;
    else if (char === ")") closeCount++;
  }

  // Remove trailing ')' characters that exceed open parens
  let result = url;
  while (closeCount > openCount && result.endsWith(")")) {
    result = result.slice(0, -1);
    closeCount--;
  }

  return result;
}

/**
 * Find all URL matches in a string with their positions.
 * Applies balanced parentheses cleanup to handle URLs in prose.
 */
function findUrls(text: string): Array<{ url: string; start: number; end: number }> {
  const matches: Array<{ url: string; start: number; end: number }> = [];
  let match;

  // Reset regex state
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const rawUrl = match[0];
    const cleanedUrl = cleanupTrailingParens(rawUrl);

    matches.push({
      url: cleanedUrl,
      start: match.index,
      end: match.index + cleanedUrl.length,
    });
  }

  return matches;
}

/**
 * Check if a position is already inside a link mark.
 */
function isInLink(view: EditorView, pos: number): boolean {
  const $pos = view.state.doc.resolve(pos);
  const linkMark = schema.marks.link;
  return $pos.marks().some((mark) => mark.type === linkMark);
}

/**
 * Create the auto link plugin.
 */
export function createAutoLinkPlugin(): Plugin {
  return new Plugin({
    key: autoLinkPluginKey,

    props: {
      // Handle typing - detect URLs when pressing space or enter
      handleKeyDown(view, event) {
        // Only process space or enter after potential URL
        if (event.key !== " " && event.key !== "Enter") {
          return false;
        }

        const { state } = view;
        const { selection } = state;

        // Only work with cursor selections (not ranges)
        if (!selection.empty) {
          return false;
        }

        const $cursor = selection.$from;
        const textBefore = $cursor.parent.textBetween(
          0,
          $cursor.parentOffset,
          undefined,
          "\ufffc"
        );

        // Find URLs in the text before cursor
        const urls = findUrls(textBefore);
        if (urls.length === 0) {
          return false;
        }

        // Check the last URL - it should end at cursor position
        const lastUrl = urls[urls.length - 1];
        if (lastUrl.end !== textBefore.length) {
          return false;
        }

        // Calculate absolute positions
        const startOfParent = $cursor.start();
        const urlStart = startOfParent + lastUrl.start;
        const urlEnd = startOfParent + lastUrl.end;

        // Don't linkify if already in a link
        if (isInLink(view, urlStart)) {
          return false;
        }

        // Apply link mark to the URL
        const linkMark = schema.marks.link.create({ href: lastUrl.url });
        const tr = state.tr.addMark(urlStart, urlEnd, linkMark);

        view.dispatch(tr);

        // Let the keypress through (don't prevent default)
        return false;
      },

      // Handle paste - find and linkify URLs in pasted content
      handlePaste(view) {
        // Let the pasteStripPlugin handle the paste first
        // We'll process the result in a transaction after

        // Schedule URL detection for after the paste is processed
        setTimeout(() => {
          linkifySelection(view);
        }, 0);

        // Don't prevent the default paste handling
        return false;
      },
    },
  });
}

/**
 * Find and linkify URLs in the current selection or recently pasted text.
 * This is called after paste to process the inserted content.
 */
function linkifySelection(view: EditorView): void {
  // Guard against destroyed view (can happen if editor unmounts during setTimeout)
  if (!view.state) return;

  const { state } = view;
  const { doc } = state;

  // Get the text content of the document to find URLs
  let tr = state.tr;
  let modified = false;

  // Scan all text nodes for URLs
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;

    const urls = findUrls(node.text);
    if (urls.length === 0) return;

    // Process URLs in reverse order to maintain positions
    for (let i = urls.length - 1; i >= 0; i--) {
      const urlInfo = urls[i];
      const from = pos + urlInfo.start;
      const to = pos + urlInfo.end;

      // Check if already has link mark
      const $from = doc.resolve(from);
      const hasLink = $from.marks().some((mark) => mark.type === schema.marks.link);

      if (!hasLink) {
        const linkMark = schema.marks.link.create({ href: urlInfo.url });
        tr = tr.addMark(from, to, linkMark);
        modified = true;
      }
    }
  });

  if (modified) {
    view.dispatch(tr);
  }
}

/**
 * Manually trigger URL linkification on the current document.
 * Useful for processing existing content.
 */
export function linkifyDocument(view: EditorView): void {
  linkifySelection(view);
}
